import { useCallback, useEffect, useRef, useState } from "react";
import {
  PaletteColor,
  PaletteColorHex,
  getComplementColor,
} from "../../types/PaletteColor.ts";
import { SandboxScreen } from "./SandboxScreen.tsx";
import { DashboardSandboxScreen } from "./DashboardSandboxScreen.tsx";
import { SpotlightOverlay, type SpotlightRect } from "./SpotlightOverlay.tsx";
import { BASIC_TUTORIAL_STEPS } from "./basicTutorialSteps.ts";
import { SETTINGS_TUTORIAL_STEPS } from "./settingsTutorialSteps.ts";
import { CUSTOM_TUTORIAL_STEPS } from "./customTutorialSteps.ts";
import { RECURRENCE_TUTORIAL_STEPS } from "./recurrenceTutorialSteps.ts";
import { REMINDER_TUTORIAL_STEPS } from "./reminderTutorialSteps.ts";
import { DASHBOARD_TUTORIAL_STEPS } from "./dashboardTutorialSteps.ts";
import type {
  SandboxAutoReminderForm,
  SandboxCustomColumn,
  SandboxList,
  SandboxPriority,
  SandboxReminder,
  SandboxStatus,
  SandboxStatusDef,
  SandboxTag,
  SandboxTask,
  SandboxTaskDraft,
  SettingsTab,
  TutorialAction,
} from "./types.ts";
import { SANDBOX_DEFAULT_STATUSES, SANDBOX_TAGS } from "./types.ts";

/** タスク作成ダイアログの初期状態 */
const emptyTaskDraft: SandboxTaskDraft = {
  open: false,
  title: "",
  priority: "なし",
  startDate: undefined,
  endDate: undefined,
  assignees: [],
  requesters: [],
  tags: [],
  customValues: {},
  memo: "",
};

interface Props {
  paletteColor: PaletteColor;
  isDark?: boolean;
  /** 完了者として表示する名前（未指定なら「あなた」） */
  currentUserName?: string;
  /** チュートリアルの種類（既定は基本） */
  variant?:
    | "basic"
    | "settings"
    | "custom"
    | "recurrence"
    | "reminder"
    | "dashboard";
  onClose: () => void;
}

let idCounter = 0;
const nextId = (prefix: string): string => `${prefix}-${++idCounter}`;

/** スキップで操作を代行するときに補完する既定値（ステップ本文の例に合わせる） */
const SKIP_PROJECT_NAME = "サンプルプロジェクト";
const SKIP_TASK_TITLE = "サンプルタスク";

/** 設定チュートリアルの初期データ（プロジェクト・タスクを用意済み） */
const createSettingsSeed = (): SandboxList[] => [
  {
    id: nextId("list"),
    name: "営業チーム",
    tasks: [
      {
        id: nextId("task"),
        title: "見積書を作成する",
        status: "未対応",
        completed: false,
        priority: "中",
        memo: "",
        assignees: ["あなた"],
        requesters: [],
        tags: [],
        customValues: {},
      },
    ],
  },
];

/** 繰り返しチュートリアルの初期データ（作成元タスクを用意済み） */
const createRecurrenceSeed = (): SandboxList[] => [
  {
    id: nextId("list"),
    name: "経理チーム",
    tasks: [
      {
        id: nextId("task"),
        title: "月次レポートを作成する",
        status: "未対応",
        completed: false,
        priority: "なし",
        memo: "",
        assignees: ["あなた"],
        requesters: [],
        tags: [],
        customValues: {},
      },
    ],
  },
];

/** YYYY-MM-DD 形式に整形する */
const toYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** リマインダーチュートリアルの初期データ（期限付きのタスクを用意済み） */
const createReminderSeed = (): SandboxList[] => {
  // 期限は「1週間後」に設定し、自動リマインダーの対象になるようにする
  const deadline = new Date();
  deadline.setDate(deadline.getDate() + 7);
  const deadlineYmd = toYmd(deadline);
  return [
    {
      id: nextId("list"),
      name: "総務チーム",
      tasks: [
        {
          id: nextId("task"),
          title: "備品を発注する",
          status: "未対応",
          completed: false,
          priority: "中",
          memo: "",
          startDate: deadlineYmd,
          endDate: deadlineYmd,
          assignees: ["あなた"],
          requesters: [],
          // タグでの絞り込みを体験できるよう、あらかじめ「重要」タグを付けておく
          tags: ["重要"],
          customValues: {},
          reminders: [],
        },
      ],
    },
  ];
};

/** 自動リマインダーフォームの初期値 */
const defaultAutoReminderForm = (): SandboxAutoReminderForm => ({
  isEnabled: false,
  ruleName: "",
  daysBefore: 1,
  hour: 9,
  priorities: ["高", "中", "低", "なし"],
  statuses: ["未対応", "対応中"],
  // タグ未指定 = タグ条件なし（既定では全タグのタスクが対象）
  tags: [],
  applyToExisting: true,
});

/** 期限から「N日前 H時」の通知日時ラベルを組み立てる */
const buildAutoReminderLabel = (
  endDate: string | undefined,
  daysBefore: number,
  hour: number,
): string | null => {
  if (!endDate) return null;
  const d = new Date(endDate);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() - daysBefore);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(hour).padStart(2, "0");
  return `${y}/${m}/${day} ${hh}:00`;
};

/**
 * 基本チュートリアルの全画面サンドボックス。
 *
 * - サンドボックス（再現画面）の状態をすべて保持し、DB・API は一切呼ばない。
 * - スポットライトのハイライト対象は data-tut 属性を querySelector で測定して追従する。
 * - ユーザー操作（notifyAction）が現在ステップの expectAction と一致したら次へ進む。
 */
export const TutorialSandbox = ({
  paletteColor,
  isDark = false,
  currentUserName = "あなた",
  variant = "basic",
  onClose,
}: Props) => {
  const accent = PaletteColorHex[paletteColor];
  // 説明ダイアログ（SpotlightOverlay）は、実画面を模したサンドボックスと
  // 見分けやすいよう、設定色の補色に当たる PaletteColor を使う。
  const overlayAccent = PaletteColorHex[getComplementColor(paletteColor)];
  // 設定系（ステータス・タグ）とカスタム項目はどちらも「全体設定ダイアログ」を使う。
  const isSettings = variant === "settings";
  const isCustom = variant === "custom";
  const isRecurrence = variant === "recurrence";
  const isReminder = variant === "reminder";
  const isDashboard = variant === "dashboard";
  const isSettingsLike = isSettings || isCustom;
  // プロジェクト・タスクを用意済みで開始する種類
  const isPreSeeded = isSettingsLike || isRecurrence || isReminder;
  const steps = isSettings
    ? SETTINGS_TUTORIAL_STEPS
    : isCustom
      ? CUSTOM_TUTORIAL_STEPS
      : isRecurrence
        ? RECURRENCE_TUTORIAL_STEPS
        : isReminder
          ? REMINDER_TUTORIAL_STEPS
          : isDashboard
            ? DASHBOARD_TUTORIAL_STEPS
            : BASIC_TUTORIAL_STEPS;

  const [stepIndex, setStepIndex] = useState(0);
  const step = steps[stepIndex];

  // ===== サンドボックス状態 =====
  // 設定・繰り返しチュートリアルはプロジェクト・タスクを用意済みで開始する
  const [seed] = useState<SandboxList[]>(() =>
    isSettingsLike
      ? createSettingsSeed()
      : isRecurrence
        ? createRecurrenceSeed()
        : isReminder
          ? createReminderSeed()
          : [],
  );
  const [lists, setLists] = useState<SandboxList[]>(seed);
  const [activeListId, setActiveListId] = useState<string | null>(
    isPreSeeded ? (seed[0]?.id ?? null) : null,
  );
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [listDialog, setListDialog] = useState({ open: false, name: "" });
  const [taskDialog, setTaskDialog] =
    useState<SandboxTaskDraft>(emptyTaskDraft);

  // ===== ルームごとの設定（設定チュートリアル用） =====
  const [statuses, setStatuses] = useState<SandboxStatusDef[]>(() => [
    ...SANDBOX_DEFAULT_STATUSES,
  ]);
  // リマインダーチュートリアルでは自動リマインダーのタグ絞り込みを体験するため、
  // 練習用タグをあらかじめ用意しておく（設定系チュートリアルでは空から追加する）。
  const [tags, setTags] = useState<SandboxTag[]>(() =>
    isReminder ? [...SANDBOX_TAGS] : [],
  );
  const [customColumns, setCustomColumns] = useState<SandboxCustomColumn[]>([]);
  const [settings, setSettings] = useState<{ open: boolean; tab: SettingsTab }>(
    { open: false, tab: "status" },
  );
  // カスタム項目チュートリアル用: 「表示するカスタム項目」ダイアログと表示中のID
  const [customDisplay, setCustomDisplay] = useState<{ open: boolean }>({
    open: false,
  });
  const [visibleCustomColumnIds, setVisibleCustomColumnIds] = useState<number[]>(
    [],
  );
  const tagIdRef = useRef(100);
  const columnIdRef = useRef(100);

  // ===== 繰り返し作成チュートリアル用 =====
  const [recurrenceRule, setRecurrenceRule] = useState<{
    title: string;
    summary: string;
    priority: SandboxPriority;
    memo: string;
  } | null>(null);
  const [recurrenceDialog, setRecurrenceDialog] = useState({
    open: false,
    showForm: false,
  });
  const [recurrenceListOpen, setRecurrenceListOpen] = useState(false);
  const [updatePrompt, setUpdatePrompt] = useState<{
    open: boolean;
    isOriginal: boolean;
  }>({ open: false, isOriginal: true });
  // 自動作成されたタスクの ID（日付経過ステップで追加される）
  const autoTaskIdRef = useRef<string | null>(null);

  // ===== リマインダーチュートリアル用 =====
  const [reminderDialog, setReminderDialog] = useState({ open: false });
  const [autoReminderForm, setAutoReminderForm] =
    useState<SandboxAutoReminderForm>(defaultAutoReminderForm);
  const [autoReminderSaved, setAutoReminderSaved] = useState(false);
  const reminderIdRef = useRef(1000);

  // ===== ステップ進行 =====
  const stepRef = useRef(step);
  stepRef.current = step;

  const notifyAction = useCallback((action: TutorialAction) => {
    if (stepRef.current?.expectAction === action) {
      setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    }
  }, [steps.length]);

  const goNext = useCallback(() => {
    setStepIndex((i) => {
      if (i >= steps.length - 1) {
        onClose();
        return i;
      }
      return i + 1;
    });
  }, [steps.length, onClose]);

  // Esc で終了
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // 繰り返しチュートリアル: 「日付が経過しました」ステップに入ったら、
  // ルールに沿って自動作成されたタスクを一覧に追加する（1回だけ）。
  useEffect(() => {
    if (step?.id !== "time-passed" || autoTaskIdRef.current) return;
    // 更新プロンプトが残っていれば閉じる
    setUpdatePrompt((p) => ({ ...p, open: false }));
    setLists((prev) => {
      const listId = activeListId ?? prev[0]?.id;
      const source = prev
        .find((l) => l.id === listId)
        ?.tasks.find((t) => !t.sourceRecurring);
      if (!source) return prev;
      const autoId = nextId("task");
      autoTaskIdRef.current = autoId;
      const autoTask: SandboxTask = {
        id: autoId,
        title: source.title,
        status: "未対応",
        completed: false,
        priority: "なし",
        memo: source.memo,
        assignees: [...source.assignees],
        requesters: [...source.requesters],
        tags: [...source.tags],
        customValues: {},
        sourceRecurring: true,
      };
      return prev.map((l) =>
        l.id === listId ? { ...l, tasks: [...l.tasks, autoTask] } : l,
      );
    });
  }, [step?.id, activeListId]);

  // ===== スポットライト測定（rAF で対象要素の矩形を追従） =====
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const rectRef = useRef<SpotlightRect | null>(null);

  useEffect(() => {
    let raf = 0;
    const sameRect = (a: SpotlightRect | null, b: SpotlightRect | null) => {
      if (a === null || b === null) return a === b;
      return (
        Math.abs(a.top - b.top) < 0.5 &&
        Math.abs(a.left - b.left) < 0.5 &&
        Math.abs(a.width - b.width) < 0.5 &&
        Math.abs(a.height - b.height) < 0.5
      );
    };
    const measure = () => {
      const key = stepRef.current?.targetKey ?? null;
      let next: SpotlightRect | null = null;
      if (key) {
        const el = document.querySelector(`[data-tut="${key}"]`) as
          | HTMLElement
          | null;
        if (el) {
          const r = el.getBoundingClientRect();
          next = { top: r.top, left: r.left, width: r.width, height: r.height };
        }
      }
      if (!sameRect(next, rectRef.current)) {
        rectRef.current = next;
        setRect(next);
      }
      raf = requestAnimationFrame(measure);
    };
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, []);

  // ステップが変わったら、対象要素をスクロール表示する。
  // ダイアログ内のスクロール領域下部にあるボタン（例: 自動リマインダーの「保存」）は、
  // スポットライトの暗転がスクロール操作を遮るため、隠れると操作できなくなる。
  // レイアウト確定後に対象を中央へスクロールして必ず見える位置へ出す。
  useEffect(() => {
    const key = step?.targetKey;
    if (!key) return;
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-tut="${key}"]`) as
        | HTMLElement
        | null;
      el?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
    return () => cancelAnimationFrame(raf);
  }, [stepIndex, step?.targetKey]);

  /**
   * 自動リマインダー設定を既存タスクへ適用する（重複防止のため付け直し）。
   * 「保存」操作と、操作待ちステップのスキップ（操作代行）の両方から使う。
   */
  const applyAutoReminderRules = (form: SandboxAutoReminderForm) => {
    if (!(form.isEnabled && form.applyToExisting)) return;
    setLists((prev) =>
      prev.map((l) =>
        l.id !== activeListId
          ? l
          : {
              ...l,
              tasks: l.tasks.map((t) => {
                // 既存の自動リマインダーは一旦除去（重複防止・付け直し）
                const manual = (t.reminders ?? []).filter((r) => !r.isAuto);
                // 対象タグ: 指定したタグをすべて持つタスクのみ一致（AND）。未指定なら条件なし
                const requiredTagNames = form.tags
                  .map((id) => SANDBOX_TAGS.find((tag) => tag.id === id)?.name)
                  .filter((name): name is string => !!name);
                const tagMatches = requiredTagNames.every((name) =>
                  t.tags.includes(name),
                );
                const matches =
                  !!t.endDate &&
                  !t.completed &&
                  t.status !== "非表示" &&
                  form.priorities.includes(t.priority) &&
                  form.statuses.includes(t.status) &&
                  tagMatches;
                if (!matches) return { ...t, reminders: manual };
                const label = buildAutoReminderLabel(
                  t.endDate,
                  form.daysBefore,
                  form.hour,
                );
                if (!label) return { ...t, reminders: manual };
                const auto: SandboxReminder = {
                  id: `reminder-auto-${++reminderIdRef.current}`,
                  label,
                  isAuto: true,
                };
                return { ...t, reminders: [...manual, auto] };
              }),
            },
      ),
    );
  };

  // ===== サンドボックス操作ハンドラ =====
  const handlers = {
    projectAdd: () => {
      setListDialog({ open: true, name: "" });
      notifyAction("open-list-dialog");
    },
    listNameChange: (v: string) => setListDialog((d) => ({ ...d, name: v })),
    listCreate: () => {
      const name = listDialog.name.trim();
      if (!name) return;
      const id = nextId("list");
      const newList: SandboxList = { id, name, tasks: [] };
      setLists((prev) => [...prev, newList]);
      setActiveListId(id);
      setSelectedTaskId(null);
      setListDialog({ open: false, name: "" });
      notifyAction("create-project");
    },
    listCancel: () => setListDialog({ open: false, name: "" }),

    fab: () => {
      setTaskDialog({ ...emptyTaskDraft, open: true });
      notifyAction("open-task-dialog");
    },
    taskTitleChange: (v: string) => setTaskDialog((d) => ({ ...d, title: v })),
    taskPriorityChange: (p: SandboxPriority) =>
      setTaskDialog((d) => ({ ...d, priority: p })),
    taskDateChange: (field: "startDate" | "endDate", v: string) =>
      setTaskDialog((d) => ({ ...d, [field]: v || undefined })),
    taskToggleAssignee: (name: string) =>
      setTaskDialog((d) => ({
        ...d,
        assignees: d.assignees.includes(name)
          ? d.assignees.filter((n) => n !== name)
          : [...d.assignees, name],
      })),
    taskToggleRequester: (name: string) =>
      setTaskDialog((d) => ({
        ...d,
        requesters: d.requesters.includes(name)
          ? d.requesters.filter((n) => n !== name)
          : [...d.requesters, name],
      })),
    taskToggleTag: (name: string) =>
      setTaskDialog((d) => ({
        ...d,
        tags: d.tags.includes(name)
          ? d.tags.filter((n) => n !== name)
          : [...d.tags, name],
      })),
    taskCustomChange: (columnId: number, v: string) =>
      setTaskDialog((d) => ({
        ...d,
        customValues: { ...d.customValues, [columnId]: v },
      })),
    taskMemoChange: (v: string) => setTaskDialog((d) => ({ ...d, memo: v })),
    taskCreate: () => {
      const title = taskDialog.title.trim();
      if (!title || !activeListId) return;
      const id = nextId("task");
      setLists((prev) =>
        prev.map((l) =>
          l.id === activeListId
            ? {
                ...l,
                tasks: [
                  ...l.tasks,
                  {
                    id,
                    title,
                    status: "未対応" as SandboxStatus,
                    completed: false,
                    priority: taskDialog.priority,
                    memo: taskDialog.memo,
                    startDate: taskDialog.startDate,
                    endDate: taskDialog.endDate,
                    assignees: taskDialog.assignees,
                    requesters: taskDialog.requesters,
                    tags: taskDialog.tags,
                    customValues: taskDialog.customValues,
                  },
                ],
              }
            : l,
        ),
      );
      setTaskDialog(emptyTaskDraft);
      notifyAction("create-task");
    },
    taskCancel: () => setTaskDialog(emptyTaskDraft),

    selectTask: (id: string) => {
      setSelectedTaskId(id);
      notifyAction("select-task");
      // 繰り返しチュートリアル: 自動作成タスクを選んだときは専用アクションも通知
      if (id === autoTaskIdRef.current) notifyAction("select-auto-task");
    },

    detailPriorityChange: (p: SandboxPriority) => {
      if (!activeListId || !selectedTaskId) return;
      // 変更有無は現在のレンダー時点の状態から判定する（updater 内で副作用を起こさない）
      const current = lists
        .find((l) => l.id === activeListId)
        ?.tasks.find((t) => t.id === selectedTaskId);
      const changed = current ? current.priority !== p : false;
      setLists((prev) =>
        prev.map((l) =>
          l.id === activeListId
            ? {
                ...l,
                tasks: l.tasks.map((t) =>
                  t.id === selectedTaskId ? { ...t, priority: p } : t,
                ),
              }
            : l,
        ),
      );
      if (changed) notifyAction("change-priority");
      // 繰り返しチュートリアル: 作成元/自動作成タスクの編集で更新プロンプトを表示
      if (changed && isRecurrence) {
        const isAuto = selectedTaskId === autoTaskIdRef.current;
        setUpdatePrompt({ open: true, isOriginal: !isAuto });
        notifyAction(isAuto ? "recurrence-edit-auto" : "recurrence-edit-source");
      }
    },

    detailMemoChange: (v: string) => {
      if (!activeListId || !selectedTaskId) return;
      setLists((prev) =>
        prev.map((l) =>
          l.id === activeListId
            ? {
                ...l,
                tasks: l.tasks.map((t) =>
                  t.id === selectedTaskId ? { ...t, memo: v } : t,
                ),
              }
            : l,
        ),
      );
    },

    toggleStatusMenu: () => {
      const willOpen = !statusMenuOpen;
      setStatusMenuOpen(willOpen);
      // 展開（開く）操作のみをステップ進行のトリガーにする
      if (willOpen) notifyAction("open-status-menu");
    },

    changeStatus: (s: SandboxStatus) => {
      if (!activeListId || !selectedTaskId) return;
      const isComplete = s === "完了";
      setLists((prev) =>
        prev.map((l) =>
          l.id === activeListId
            ? {
                ...l,
                tasks: l.tasks.map((t) =>
                  t.id === selectedTaskId
                    ? {
                        ...t,
                        status: s,
                        completed: isComplete,
                        completedAt: isComplete
                          ? new Date().toISOString()
                          : undefined,
                        completedBy: isComplete ? currentUserName : undefined,
                      }
                    : t,
                ),
              }
            : l,
        ),
      );
      setStatusMenuOpen(false);
      if (isComplete) notifyAction("complete-task");
    },

    // チェックボックスでの完了切替（指定タスクを対象にする）
    toggleComplete: (id: string) => {
      if (!activeListId) return;
      const current = lists
        .find((l) => l.id === activeListId)
        ?.tasks.find((t) => t.id === id);
      if (!current) return;
      const willComplete = !current.completed;
      setLists((prev) =>
        prev.map((l) =>
          l.id === activeListId
            ? {
                ...l,
                tasks: l.tasks.map((t) =>
                  t.id === id
                    ? {
                        ...t,
                        status: willComplete
                          ? ("完了" as SandboxStatus)
                          : ("未対応" as SandboxStatus),
                        completed: willComplete,
                        completedAt: willComplete
                          ? new Date().toISOString()
                          : undefined,
                        completedBy: willComplete ? currentUserName : undefined,
                      }
                    : t,
                ),
              }
            : l,
        ),
      );
      // 展開メニューが開いていれば閉じる
      setStatusMenuOpen(false);
      if (willComplete) notifyAction("complete-task");
    },

    toggleShowCompleted: () => {
      const next = !showCompleted;
      setShowCompleted(next);
      if (next) notifyAction("toggle-show-completed");
    },

    // ===== ルームごとの設定チュートリアル用 =====
    openSettings: () => {
      setSettings({ open: true, tab: "status" });
      notifyAction("open-settings");
    },
    closeSettings: () => {
      setSettings((s) => ({ ...s, open: false }));
      notifyAction("close-settings");
    },
    setSettingsTab: (tab: SettingsTab) => {
      setSettings((s) => ({ ...s, tab }));
      if (tab === "tags") notifyAction("open-tags-tab");
      else if (tab === "custom") notifyAction("open-custom-tab");
      else if (tab === "autoReminder") notifyAction("open-auto-reminder-tab");
    },
    addStatus: (name: string, color: string) => {
      setStatuses((prev) =>
        prev.some((s) => s.name === name) ? prev : [...prev, { name, color }],
      );
      notifyAction("add-status");
    },
    addTag: (name: string, color: string) => {
      setTags((prev) =>
        prev.some((t) => t.name === name)
          ? prev
          : [...prev, { id: ++tagIdRef.current, name, color }],
      );
      notifyAction("add-tag");
    },
    addCustomColumn: (
      name: string,
      type: "text" | "select",
      options: string[],
    ) => {
      setCustomColumns((prev) =>
        prev.some((c) => c.name === name)
          ? prev
          : [
              ...prev,
              {
                id: ++columnIdRef.current,
                name,
                type,
                ...(type === "select" ? { options } : {}),
              },
            ],
      );
      // 入力形式ごとにステップを分けているため、型に応じたアクションを通知する
      notifyAction(type === "select" ? "add-custom-select" : "add-custom-text");
    },
    detailCustomChange: (columnId: number, v: string) => {
      if (!activeListId || !selectedTaskId) return;
      setLists((prev) =>
        prev.map((l) =>
          l.id === activeListId
            ? {
                ...l,
                tasks: l.tasks.map((t) =>
                  t.id === selectedTaskId
                    ? {
                        ...t,
                        customValues: { ...t.customValues, [columnId]: v },
                      }
                    : t,
                ),
              }
            : l,
        ),
      );
    },
    openCustomDisplay: () => {
      setCustomDisplay({ open: true });
      notifyAction("open-custom-display");
    },
    closeCustomDisplay: () => setCustomDisplay({ open: false }),
    saveCustomDisplay: (visibleIds: number[]) => {
      setVisibleCustomColumnIds(visibleIds);
      setCustomDisplay({ open: false });
      notifyAction("save-custom-display");
    },
    detailOpenStatusMenu: () => {
      notifyAction("detail-open-status");
    },
    detailChangeStatus: (s: SandboxStatus) => {
      if (!activeListId || !selectedTaskId) return;
      setLists((prev) =>
        prev.map((l) =>
          l.id === activeListId
            ? {
                ...l,
                tasks: l.tasks.map((t) =>
                  t.id === selectedTaskId
                    ? {
                        ...t,
                        status: s,
                        completed: s === "完了",
                        completedAt:
                          s === "完了" ? new Date().toISOString() : undefined,
                        completedBy: s === "完了" ? currentUserName : undefined,
                      }
                    : t,
                ),
              }
            : l,
        ),
      );
    },
    detailToggleTag: (name: string) => {
      if (!activeListId || !selectedTaskId) return;
      setLists((prev) =>
        prev.map((l) =>
          l.id === activeListId
            ? {
                ...l,
                tasks: l.tasks.map((t) =>
                  t.id === selectedTaskId
                    ? {
                        ...t,
                        tags: t.tags.includes(name)
                          ? t.tags.filter((n) => n !== name)
                          : [...t.tags, name],
                      }
                    : t,
                ),
              }
            : l,
        ),
      );
    },

    // ===== 繰り返し作成チュートリアル用 =====
    openRecurrenceDialog: () => {
      // 実画面同様、まず「自動作成ルール設定」ダイアログ（導入画面）を開く
      setRecurrenceDialog({ open: true, showForm: false });
      notifyAction("recurrence-open-dialog");
    },
    showRecurrenceForm: () => {
      // 「ルールを追加」で頻度設定フォームを表示する
      setRecurrenceDialog({ open: true, showForm: true });
      notifyAction("recurrence-show-form");
    },
    closeRecurrenceDialog: () =>
      setRecurrenceDialog({ open: false, showForm: false }),
    saveRecurrence: (frequency: string) => {
      // 設定時点のタスク内容（タイトル・重要度・メモ）をコピーしてルール化する
      const source = lists
        .find((l) => l.id === activeListId)
        ?.tasks.find((t) => t.id === selectedTaskId);
      setRecurrenceRule({
        title: source?.title ?? "繰り返し作成タスク",
        summary: frequency,
        priority: source?.priority ?? "なし",
        memo: source?.memo ?? "",
      });
      setRecurrenceDialog({ open: false, showForm: false });
      notifyAction("recurrence-save");
    },
    editRulePriority: (p: SandboxPriority) => {
      setRecurrenceRule((prev) => (prev ? { ...prev, priority: p } : prev));
      notifyAction("recurrence-edit-rule");
    },
    editRuleMemo: (v: string) => {
      setRecurrenceRule((prev) => (prev ? { ...prev, memo: v } : prev));
      notifyAction("recurrence-edit-rule");
    },
    openRecurrenceList: () => {
      setRecurrenceListOpen(true);
      notifyAction("recurrence-open-list");
    },
    closeRecurrenceList: () => {
      setRecurrenceListOpen(false);
      notifyAction("recurrence-close-list");
    },
    confirmUpdatePrompt: () =>
      setUpdatePrompt((p) => ({ ...p, open: false })),
    dismissUpdatePrompt: () =>
      setUpdatePrompt((p) => ({ ...p, open: false })),

    // ===== リマインダーチュートリアル用 =====
    openReminderDialog: () => {
      setReminderDialog({ open: true });
      notifyAction("reminder-open-dialog");
    },
    closeReminderDialog: () => {
      setReminderDialog({ open: false });
      notifyAction("reminder-close");
    },
    addReminder: (label: string) => {
      if (!activeListId || !selectedTaskId) return;
      const newReminder: SandboxReminder = {
        id: `reminder-${++reminderIdRef.current}`,
        label,
        isAuto: false,
      };
      setLists((prev) =>
        prev.map((l) =>
          l.id === activeListId
            ? {
                ...l,
                tasks: l.tasks.map((t) =>
                  t.id === selectedTaskId
                    ? { ...t, reminders: [...(t.reminders ?? []), newReminder] }
                    : t,
                ),
              }
            : l,
        ),
      );
      notifyAction("reminder-add");
    },
    deleteReminder: (id: string) => {
      if (!activeListId || !selectedTaskId) return;
      setLists((prev) =>
        prev.map((l) =>
          l.id === activeListId
            ? {
                ...l,
                tasks: l.tasks.map((t) =>
                  t.id === selectedTaskId
                    ? {
                        ...t,
                        reminders: (t.reminders ?? []).filter(
                          (r) => r.id !== id,
                        ),
                      }
                    : t,
                ),
              }
            : l,
        ),
      );
    },
    toggleAutoReminder: () => {
      // 現在のレンダー時点の状態から判定し、updater 内では副作用を起こさない
      const willEnable = !autoReminderForm.isEnabled;
      setAutoReminderSaved(false);
      setAutoReminderForm((prev) => ({ ...prev, isEnabled: willEnable }));
      // 「ONにする」操作をステップ進行のトリガーにする
      if (willEnable) notifyAction("toggle-auto-reminder");
    },
    updateAutoReminderForm: (updates: Partial<SandboxAutoReminderForm>) => {
      setAutoReminderSaved(false);
      setAutoReminderForm((prev) => ({ ...prev, ...updates }));
    },
    saveAutoReminder: () => {
      setAutoReminderSaved(true);
      // 有効かつ「既存にも適用」なら、条件に合うタスクへ自動リマインダーを付け直す
      applyAutoReminderRules(autoReminderForm);
      notifyAction("save-auto-reminder");
    },
  };

  // ===== スキップ（操作の代行） =====
  // ダッシュボードは状態を DashboardSandboxScreen 側で持つため、
  // 操作代行の関数を ref 経由で登録してもらい、スキップ時に呼び出す。
  const dashboardSimulateRef = useRef<((action: TutorialAction) => void) | null>(
    null,
  );

  /**
   * 操作待ちステップの「スキップ」用に、ユーザーが取るべき操作と同等の
   * 状態変更を代わりに実行する。入力が必要な操作はステップ本文の例に合わせた
   * 既定値で補完する。ステップの進行は呼び出し元（skipStep）の notifyAction で行う。
   */
  const simulateSandboxAction = (action: TutorialAction) => {
    const list = lists.find((l) => l.id === activeListId) ?? lists[0];
    const selected = list?.tasks.find((t) => t.id === selectedTaskId);
    const patchTask = (taskId: string, patch: Partial<SandboxTask>) =>
      setLists((prev) =>
        prev.map((l) => ({
          ...l,
          tasks: l.tasks.map((t) => (t.id === taskId ? { ...t, ...patch } : t)),
        })),
      );
    // 「重要度を変更する」系ステップの代行値（今と違う値になるように選ぶ）
    const nextPriority = (p: SandboxPriority): SandboxPriority =>
      p === "高" ? "中" : "高";

    switch (action) {
      // ===== 基本 =====
      case "open-list-dialog":
        setListDialog({ open: true, name: "" });
        break;
      case "create-project": {
        const name = listDialog.name.trim() || SKIP_PROJECT_NAME;
        const id = nextId("list");
        setLists((prev) => [...prev, { id, name, tasks: [] }]);
        setActiveListId(id);
        setSelectedTaskId(null);
        setListDialog({ open: false, name: "" });
        break;
      }
      case "open-task-dialog":
        setTaskDialog({ ...emptyTaskDraft, open: true });
        break;
      case "create-task": {
        if (!list) break;
        const title = taskDialog.title.trim() || SKIP_TASK_TITLE;
        const id = nextId("task");
        setLists((prev) =>
          prev.map((l) =>
            l.id === list.id
              ? {
                  ...l,
                  tasks: [
                    ...l.tasks,
                    {
                      id,
                      title,
                      status: "未対応" as SandboxStatus,
                      completed: false,
                      priority: taskDialog.priority,
                      memo: taskDialog.memo,
                      startDate: taskDialog.startDate,
                      endDate: taskDialog.endDate,
                      assignees: taskDialog.assignees,
                      requesters: taskDialog.requesters,
                      tags: taskDialog.tags,
                      customValues: taskDialog.customValues,
                    },
                  ],
                }
              : l,
          ),
        );
        setTaskDialog(emptyTaskDraft);
        break;
      }
      case "select-task": {
        const target =
          list?.tasks.find((t) => !t.sourceRecurring) ?? list?.tasks[0];
        if (target) setSelectedTaskId(target.id);
        break;
      }
      case "select-auto-task":
        if (autoTaskIdRef.current) setSelectedTaskId(autoTaskIdRef.current);
        break;
      case "change-priority": {
        const target = selected ?? list?.tasks[0];
        if (!target) break;
        if (!selected) setSelectedTaskId(target.id);
        patchTask(target.id, { priority: nextPriority(target.priority) });
        break;
      }
      case "open-status-menu":
        setStatusMenuOpen(true);
        break;
      case "complete-task": {
        const target = selected ?? list?.tasks.find((t) => !t.completed);
        if (!target) break;
        patchTask(target.id, {
          status: "完了",
          completed: true,
          completedAt: new Date().toISOString(),
          completedBy: currentUserName,
        });
        setStatusMenuOpen(false);
        break;
      }
      case "toggle-show-completed":
        setShowCompleted(true);
        break;

      // ===== ルームごとの設定・カスタム項目 =====
      case "open-settings":
        setSettings({ open: true, tab: "status" });
        break;
      case "close-settings":
        setSettings((s) => ({ ...s, open: false }));
        break;
      case "open-tags-tab":
        setSettings((s) => ({ ...s, tab: "tags" }));
        break;
      case "open-custom-tab":
        setSettings((s) => ({ ...s, tab: "custom" }));
        break;
      case "open-auto-reminder-tab":
        setSettings((s) => ({ ...s, tab: "autoReminder" }));
        break;
      case "add-status":
        setStatuses((prev) =>
          prev.some((s) => s.name === "レビュー中")
            ? prev
            : [...prev, { name: "レビュー中", color: "#FCA733" }],
        );
        break;
      case "add-tag":
        setTags((prev) =>
          prev.some((t) => t.name === "要確認")
            ? prev
            : [
                ...prev,
                { id: ++tagIdRef.current, name: "要確認", color: "#D67EF6" },
              ],
        );
        break;
      case "add-custom-text":
        setCustomColumns((prev) =>
          prev.some((c) => c.name === "見積金額")
            ? prev
            : [
                ...prev,
                { id: ++columnIdRef.current, name: "見積金額", type: "text" },
              ],
        );
        break;
      case "add-custom-select":
        setCustomColumns((prev) =>
          prev.some((c) => c.name === "カテゴリ")
            ? prev
            : [
                ...prev,
                {
                  id: ++columnIdRef.current,
                  name: "カテゴリ",
                  type: "select",
                  options: ["開発", "営業", "総務"],
                },
              ],
        );
        break;
      case "open-custom-display":
        setCustomDisplay({ open: true });
        break;
      case "save-custom-display":
        setVisibleCustomColumnIds(customColumns.map((c) => c.id));
        setCustomDisplay({ open: false });
        break;

      // ===== 繰り返し作成 =====
      case "recurrence-open-dialog":
        setRecurrenceDialog({ open: true, showForm: false });
        break;
      case "recurrence-show-form":
        setRecurrenceDialog({ open: true, showForm: true });
        break;
      case "recurrence-save": {
        const source = selected ?? list?.tasks[0];
        setRecurrenceRule({
          title: source?.title ?? "繰り返し作成タスク",
          summary: "毎週月曜日",
          priority: source?.priority ?? "なし",
          memo: source?.memo ?? "",
        });
        setRecurrenceDialog({ open: false, showForm: false });
        break;
      }
      case "recurrence-open-list":
        setRecurrenceListOpen(true);
        break;
      case "recurrence-close-list":
        setRecurrenceListOpen(false);
        break;
      case "recurrence-edit-rule":
        setRecurrenceRule((prev) =>
          prev ? { ...prev, priority: nextPriority(prev.priority) } : prev,
        );
        break;
      case "recurrence-edit-source": {
        const target = list?.tasks.find((t) => !t.sourceRecurring);
        if (target)
          patchTask(target.id, { priority: nextPriority(target.priority) });
        setUpdatePrompt({ open: true, isOriginal: true });
        break;
      }
      case "recurrence-edit-auto": {
        const target = list?.tasks.find((t) => t.id === autoTaskIdRef.current);
        if (target)
          patchTask(target.id, { priority: nextPriority(target.priority) });
        setUpdatePrompt({ open: true, isOriginal: false });
        break;
      }

      // ===== リマインダー =====
      case "reminder-open-dialog":
        setReminderDialog({ open: true });
        break;
      case "reminder-close":
        setReminderDialog({ open: false });
        break;
      case "reminder-add": {
        const target = selected ?? list?.tasks[0];
        if (!target) break;
        // 実画面の追加フォームと同じ「YYYY/MM/DD HH:mm」形式（既定 = 期限日の 08:00）
        const ymd = (target.endDate ?? toYmd(new Date())).replace(/-/g, "/");
        const newReminder: SandboxReminder = {
          id: `reminder-${++reminderIdRef.current}`,
          label: `${ymd} 08:00`,
          isAuto: false,
        };
        patchTask(target.id, {
          reminders: [...(target.reminders ?? []), newReminder],
        });
        break;
      }
      case "toggle-auto-reminder":
        setAutoReminderSaved(false);
        setAutoReminderForm((prev) => ({ ...prev, isEnabled: true }));
        break;
      case "save-auto-reminder": {
        const form = { ...autoReminderForm, isEnabled: true };
        setAutoReminderForm(form);
        setAutoReminderSaved(true);
        applyAutoReminderRules(form);
        break;
      }

      default:
        break;
    }
  };

  /**
   * 操作待ちステップをスキップする。取るべき操作を代行してから、
   * 通常操作と同じ notifyAction 経由で次のステップへ進める。
   */
  const skipStep = () => {
    const action = stepRef.current?.expectAction;
    if (!action) return;
    if (action.startsWith("dash-")) {
      // ダッシュボード側が操作の代行と onAction（＝ステップ進行）まで行う
      dashboardSimulateRef.current?.(action);
      return;
    }
    simulateSandboxAction(action);
    notifyAction(action);
  };

  const isInfoStep = !step?.expectAction;
  const isLast = stepIndex === steps.length - 1;

  // 繰り返し系の機能は繰り返しチュートリアルのときだけ有効化する。
  const recurrenceHandlers = isRecurrence
    ? {
        openRecurrenceDialog: handlers.openRecurrenceDialog,
        showRecurrenceForm: handlers.showRecurrenceForm,
        closeRecurrenceDialog: handlers.closeRecurrenceDialog,
        saveRecurrence: handlers.saveRecurrence,
        openRecurrenceList: handlers.openRecurrenceList,
        closeRecurrenceList: handlers.closeRecurrenceList,
        editRulePriority: handlers.editRulePriority,
        editRuleMemo: handlers.editRuleMemo,
        confirmUpdatePrompt: handlers.confirmUpdatePrompt,
        dismissUpdatePrompt: handlers.dismissUpdatePrompt,
      }
    : {
        openRecurrenceDialog: undefined,
        showRecurrenceForm: undefined,
        closeRecurrenceDialog: undefined,
        saveRecurrence: undefined,
        openRecurrenceList: undefined,
        closeRecurrenceList: undefined,
        editRulePriority: undefined,
        editRuleMemo: undefined,
        confirmUpdatePrompt: undefined,
        dismissUpdatePrompt: undefined,
      };

  // リマインダー系の機能はリマインダーチュートリアルのときだけ有効化する。
  const reminderHandlers = isReminder
    ? {
        openReminderDialog: handlers.openReminderDialog,
        closeReminderDialog: handlers.closeReminderDialog,
        addReminder: handlers.addReminder,
        deleteReminder: handlers.deleteReminder,
        toggleAutoReminder: handlers.toggleAutoReminder,
        updateAutoReminderForm: handlers.updateAutoReminderForm,
        saveAutoReminder: handlers.saveAutoReminder,
      }
    : {
        openReminderDialog: undefined,
        closeReminderDialog: undefined,
        addReminder: undefined,
        deleteReminder: undefined,
        toggleAutoReminder: undefined,
        updateAutoReminderForm: undefined,
        saveAutoReminder: undefined,
      };

  // 基本チュートリアルでは設定系の機能（歯車・設定ダイアログ・詳細パネルの
  // ステータス/タグ操作）を無効化し、従来どおりの挙動を保つ。
  const screenOn = isSettingsLike
    ? { ...handlers, ...recurrenceHandlers, ...reminderHandlers }
    : isReminder
      ? {
          // 全体設定ダイアログ（自動リマインダータブ）の開閉・タブ切替は使うため有効。
          // ステータス/タグ/カスタム項目の追加・詳細操作は使わないため無効化する。
          ...handlers,
          addStatus: undefined,
          addTag: undefined,
          addCustomColumn: undefined,
          detailOpenStatusMenu: undefined,
          detailChangeStatus: undefined,
          detailToggleTag: undefined,
          detailCustomChange: undefined,
          openCustomDisplay: undefined,
          closeCustomDisplay: undefined,
          saveCustomDisplay: undefined,
          ...recurrenceHandlers,
          ...reminderHandlers,
        }
      : {
          ...handlers,
          openSettings: undefined,
          closeSettings: undefined,
          setSettingsTab: undefined,
          addStatus: undefined,
          addTag: undefined,
          addCustomColumn: undefined,
          detailOpenStatusMenu: undefined,
          detailChangeStatus: undefined,
          detailToggleTag: undefined,
          detailCustomChange: undefined,
          openCustomDisplay: undefined,
          closeCustomDisplay: undefined,
          saveCustomDisplay: undefined,
          ...recurrenceHandlers,
          ...reminderHandlers,
        };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        backgroundColor: isDark ? "#121212" : "#f5f5f5",
      }}
    >
      {isDashboard ? (
        <DashboardSandboxScreen
          accent={accent}
          isDark={isDark}
          onAction={notifyAction}
          simulateRef={dashboardSimulateRef}
        />
      ) : (
        <SandboxScreen
        accent={accent}
        isDark={isDark}
        lists={lists}
        activeListId={activeListId}
        selectedTaskId={selectedTaskId}
        showCompleted={showCompleted}
        statusMenuOpen={statusMenuOpen}
        listDialog={listDialog}
        taskDialog={taskDialog}
        {...(isSettingsLike
          ? {
              statuses,
              tags,
              customColumns,
              settings,
              customDisplay,
              visibleCustomColumnIds,
            }
          : {})}
        {...(isReminder
          ? {
              statuses,
              tags,
              customColumns,
              settings,
              reminderDialog,
              autoReminderForm,
              autoReminderSaved,
            }
          : {})}
        {...(isRecurrence
          ? {
              recurrenceDialog,
              recurrenceRule,
              recurrenceListOpen,
              updatePrompt,
            }
          : {})}
        on={screenOn}
      />
      )}

      {step && (
        <SpotlightOverlay
          rect={rect}
          title={step.title}
          body={step.body}
          showNext={isInfoStep}
          isLast={isLast}
          stepIndex={stepIndex}
          totalSteps={steps.length}
          accent={overlayAccent}
          isDark={isDark}
          onNext={goNext}
          onSkip={skipStep}
          onExit={onClose}
        />
      )}
    </div>
  );
};
